// Enhanced API Client with JWT authentication and automatic token refresh
// Multi-tenant support with subdomain-based routing
import { jwtAuthService } from '../services/jwtAuthService';
import { authFetch } from '../services/authFetch';

// Get reliable API URL
function getReliableApiUrl(): string {
  const hostname = window.location.hostname;
  
  // For development (localhost), always use localhost:8000 regardless of subdomain
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    const protocol = window.location.protocol;
    return `${protocol}//localhost:8000`;
  }
  
  // For production, use same origin (nginx proxies /v1/ to backend)
  return window.location.origin;
}

const API_URL = getReliableApiUrl();

interface ApiResponse<T = any> {
  data: T;
  error?: string;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Get valid access token (will refresh if needed)
    const token = await jwtAuthService.getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }

  private handle401(): ApiResponse<never> {
    // Token is stale or missing. Clear local auth state and bounce to
    // /login. Without this, every page on the app keeps firing API
    // calls and showing error toasts after the session has expired.
    if (typeof window !== "undefined") {
      const onLogin = window.location.pathname.startsWith("/login");
      // Avoid clearing during the login flow itself.
      if (!onLogin) {
        try {
          localStorage.removeItem("jwt_access_token");
          localStorage.removeItem("jwt_refresh_token");
          localStorage.removeItem("jwt_token_expiry");
        } catch {}
        const here = window.location.pathname + window.location.search;
        window.location.replace(`/login?redirect=${encodeURIComponent(here)}`);
      }
    }
    return { data: null as never, error: "Authentication required" };
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const contentType = response.headers.get("content-type");

    // Check if response is JSON
    if (contentType && contentType.includes("application/json")) {
      try {
        const data = await response.json();

        if (!response.ok) {
          // Handle authentication errors
          if (response.status === 401) {
            return this.handle401() as ApiResponse<T>;
          }

          return {
            data: null as T,
            error: data.message || data.detail || `HTTP ${response.status}: ${response.statusText}`
          };
        }

        return { data };
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        return {
          data: null as T,
          error: "Invalid JSON response from server"
        };
      }
    } else {
      // Non-JSON response (likely HTML error page)
      await response.text(); // Consume the response body

      if (response.status === 401) {
        return this.handle401() as ApiResponse<T>;
      }

      return {
        data: null as T,
        error: `Server returned non-JSON response: ${response.status} ${response.statusText}`
      };
    }
  }

  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    try {
      const url = new URL(`${this.baseURL}${endpoint}`);
      if (params) {
        Object.keys(params).forEach(key => {
          if (params[key] !== undefined && params[key] !== null) {
            url.searchParams.append(key, String(params[key]));
          }
        });
      }

      const headers = await this.getAuthHeaders();
      const response = await authFetch(url.toString(), {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error(`API GET error for ${endpoint}:`, error);
      return {
        data: null as T,
        error: error instanceof Error ? error.message : "Network error"
      };
    }
  }

  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await authFetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error(`API POST error for ${endpoint}:`, error);
      return {
        data: null as T,
        error: error instanceof Error ? error.message : "Network error"
      };
    }
  }

  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await authFetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error(`API PUT error for ${endpoint}:`, error);
      return {
        data: null as T,
        error: error instanceof Error ? error.message : "Network error"
      };
    }
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await authFetch(`${this.baseURL}${endpoint}`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error(`API PATCH error for ${endpoint}:`, error);
      return {
        data: null as T,
        error: error instanceof Error ? error.message : "Network error"
      };
    }
  }

  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await authFetch(`${this.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error(`API DELETE error for ${endpoint}:`, error);
      return {
        data: null as T,
        error: error instanceof Error ? error.message : "Network error"
      };
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient(API_URL);

// Export helper functions for common patterns
export const safeApiCall = async <T>(
  apiCall: () => Promise<ApiResponse<T>>,
  fallbackData: T
): Promise<T> => {
  const result = await apiCall();
  if (result.error) {
    console.warn("API call failed, using fallback data:", result.error);
    return fallbackData;
  }
  return result.data;
};

export const isAuthError = (error: any): boolean => {
  return error && (
    error.message === "Authentication required" ||
    error.includes("Authentication required") ||
    error.includes("401")
  );
};
